/**
 * Pont vers le secours d'amorçage, script inline d'`index.html` : seul code qui survit à la perte du
 * bundle, il tient donc seul le budget des relances et l'écran plein. Ce module ne fait que le joindre.
 */
import { useEffect, useState } from 'react'

export type EtatReprise = 'relance' | 'hors-ligne' | 'epuise'

export interface RepriseChargement {
  echecChargement(): void
  echecRendu(): void
  planifier(suivi?: (etat: EtatReprise) => void): void
  annuler(suivi?: (etat: EtatReprise) => void): void
  recharger(): void
}

declare global {
  interface Window {
    __doitReprise?: RepriseChargement
  }
}

// Chromium, Firefox, Safari ; préchargement CSS de Vite ; morceau d'un ancien déploiement servi en HTML.
const MESSAGES_PERTE = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|is not a valid JavaScript MIME type/i

/** Vrai pour un import dynamique perdu. Un `fetch` en échec ou un bogue de rendu n'en sont pas. */
export function estErreurChargementModule(erreur: unknown): boolean {
  if (!(erreur instanceof Error)) return false
  return MESSAGES_PERTE.test(erreur.message)
}

export function repriseChargement(): RepriseChargement | undefined {
  return window.__doitReprise
}

/**
 * Pour une zone qui reste montée malgré la perte (coquille : ses dialogues portent peut-être une saisie) : planifie la
 * relance sans démonter, et rend l'état à annoncer, ou `null` s'il n'y a pas de perte.
 */
export function useReprisePerte(erreur: unknown): EtatReprise | null {
  const perdu = estErreurChargementModule(erreur)
  const [etat, setEtat] = useState<EtatReprise>('relance')
  useEffect(() => {
    if (!perdu) return undefined
    const reprise = repriseChargement()
    if (!reprise) { setEtat('epuise'); return undefined }
    reprise.planifier(setEtat)
    return () => { reprise.annuler(setEtat) }
  }, [perdu])
  return perdu ? etat : null
}
